const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '19 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, scholarly, and ecclesial sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

const source = (id, author, title, detail, href) => ({ id, author, title, detail, href });

export const womenMinistryPerspectives = [
    {
        slug: 'can-women-be-pastors',
        category: 'Church Leadership',
        question: 'Can women be pastors?',
        shortTitle: 'Can women serve as pastors?',
        summary: 'Christians agree that women are equal bearers of God’s image, receive spiritual gifts, and perform vital ministries in the church. They disagree over whether the pastoral office is open to women, reserved to qualified men while many other ministries remain open, or—within Catholic sacramental theology—belongs to a male priesthood that is not identical to Protestant understandings of a pastor or elder.',
        introduction: 'The word “pastor” does not name exactly the same office in every tradition. Many Protestants identify pastor, elder, and overseer as one governing and teaching office; other churches distinguish senior pastors, elders, ministers, and licensed preachers; Catholic teaching approaches the question through sacramental priesthood. The readings therefore compare both biblical interpretation and competing accounts of what ordained church leadership is.',
        seoDescription: 'Compare biblical and Christian perspectives on women pastors: egalitarian ministry, male pastor-eldership, and Catholic male priesthood.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Acts 2:14–21', href: '/read/Acts.2/#Acts.2.14-Acts.2.21' },
            { label: 'Romans 16:1–16', href: '/read/Rom.16/#Rom.16.1-Rom.16.16' },
            { label: '1 Timothy 2:8–15', href: '/read/1Tim.2/#1Tim.2.8-1Tim.2.15' },
            { label: '1 Timothy 3:1–13', href: '/read/1Tim.3/#1Tim.3.1-1Tim.3.13' },
        ],
        readings: [
            {
                id: 'gifted-for-all-pastoral-offices',
                title: 'Women may serve as pastors when called and gifted',
                thesis: 'Women and men who are called, gifted, formed, and recognised by the church may serve in every pastoral and governing office.',
                accent: 'teal',
                badges: ['Egalitarian', 'Pentecostal', 'Anglican', 'Moderate'],
                summary: 'This reading interprets Pentecost’s gift of prophetic speech to daughters and sons, women’s leadership across Scripture, and Paul’s female coworkers as evidence that ministry is distributed by the Spirit rather than restricted by sex. It reads 1 Timothy 2 as addressing a particular disorder or unauthorised teaching in Ephesus, not establishing a permanent barrier to women who are properly taught, tested, and called.',
                contribution: 'It integrates the church’s teaching about spiritual gifts with its practices of recognition and appointment, and it gives substantial weight to women who prophesied, taught, hosted churches, carried apostolic letters, and laboured alongside Paul. It also asks churches to judge leaders by character and gifting rather than by assumptions about sex.',
                caution: 'This view must give a persuasive account of 1 Timothy 2:12–15 and the male language in elder qualifications rather than allowing narrative examples or contemporary experience to bypass restrictive texts. It must also avoid treating every gift of speech as automatically equivalent to pastoral office.',
                supporters: [
                    { name: 'Assemblies of God (USA)', sourceId: 'ag-women-leadership' },
                    { name: 'Church of England', sourceId: 'church-england-women-bishops' },
                    { name: 'Philip B. Payne', sourceId: 'payne-one-in-christ' },
                ],
                predicates: [
                    {
                        label: '1 Timothy 2 addresses unauthorised or locally harmful teaching rather than every properly recognised female teacher',
                        topicSlug: 'what-does-1-timothy-2-12-mean',
                        readingId: 'combined-prohibition-on-assumed-authority',
                        linked: true,
                    },
                    {
                        label: 'Biblical women provide positive patterns of authoritative ministry and leadership',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'patterns-of-authoritative-ministry',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'male-pastor-elder-office', linked: true },
                    { type: 'contrasts with', target: 'male-sacramental-priesthood', linked: true },
                    {
                        type: 'raises',
                        label: 'What does 1 Timothy 2:12 mean?',
                        topicSlug: 'what-does-1-timothy-2-12-mean',
                        linked: true,
                    },
                ],
            },
            {
                id: 'male-pastor-elder-office',
                title: 'Pastor-elder is an office for men',
                thesis: 'Women may exercise many significant ministries, but Scripture reserves the governing and authoritative teaching office of pastor, elder, or overseer to qualified men.',
                accent: 'navy',
                badges: ['Complementarian', 'Conservative', 'Baptist', 'Reformed'],
                summary: 'This reading identifies pastor, elder, and overseer as names for one New Testament office. It understands 1 Timothy 2:12 to restrict the teaching and authority that characterise that office and reads the creation rationale in verses 13–14 as extending beyond a temporary Ephesian problem. Women may pray, prophesy, teach other women and children, evangelise, serve as missionaries, and contribute substantially without holding that office.',
                contribution: 'It takes the restrictive texts and the creation appeal at face value while distinguishing the pastorate from Christian ministry in general. Its better forms explicitly honour women’s gifts and resist the mistaken conclusion that every man has authority over every woman.',
                caution: 'The view must explain why women such as Phoebe, Priscilla, Junia, and other coworkers do not challenge its office boundaries, how consistently “pastor”, “elder”, and “overseer” function as one office, and whether churches sometimes restrict women far beyond the actual claim of the position.',
                supporters: [
                    { name: 'Southern Baptist Convention', sourceId: 'sbc-pastor-elder' },
                    { name: 'Thomas R. Schreiner and Andreas J. Köstenberger', sourceId: 'women-in-the-church' },
                    { name: 'Greg Gilbert', sourceId: 'gilbert-women-pastors' },
                ],
                predicates: [
                    {
                        label: '1 Timothy 2 gives an enduring restriction on the church’s authoritative teaching office',
                        topicSlug: 'what-does-1-timothy-2-12-mean',
                        readingId: 'enduring-church-order',
                        linked: true,
                    },
                    {
                        label: 'Women’s biblical ministries are extensive without identifying them as pastor-elders',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'broad-ministry-with-office-distinction',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'gifted-for-all-pastoral-offices', linked: true },
                    { type: 'overlaps with', target: 'male-sacramental-priesthood', linked: true },
                    {
                        type: 'raises',
                        label: 'What do women leaders in the Bible show?',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        linked: true,
                    },
                ],
            },
            {
                id: 'male-sacramental-priesthood',
                title: 'Ordained priesthood is reserved for men',
                thesis: 'The church has no authority to confer priestly ordination upon women, although women exercise indispensable non-priestly ministries and possess equal dignity and holiness.',
                accent: 'red',
                badges: ['Catholic', 'Sacramental', 'Traditional', 'Conservative'],
                summary: 'Catholic teaching does not begin by equating a parish priest with the congregational pastor-elder of Baptist or Reformed polity. It understands ordination sacramentally, as participation in an apostolic ministry of teaching, sanctifying, and governing in which the priest represents Christ in a particular ecclesial way. The reservation is grounded in Christ’s selection of the Twelve and the church’s constant reception of that pattern, not in a claim that women are less gifted or holy.',
                contribution: 'It reminds the debate that conclusions depend partly upon ecclesiology: what ordination is, who may define it, and how Scripture and apostolic tradition function together. It also separates spiritual dignity from eligibility for every ecclesial role.',
                caution: 'Those outside this sacramental framework question whether the male Twelve establish a permanent sex requirement, whether later tradition faithfully preserves or culturally narrows apostolic practice, and whether representing Christ requires a male priest.',
                supporters: [
                    { name: 'Pope John Paul II', sourceId: 'ordinatio-sacerdotalis' },
                ],
                predicates: [
                    {
                        label: 'Apostolic commands and patterns are authoritatively received through the church’s living tradition',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'ecclesial-reception',
                        linked: true,
                    },
                    {
                        label: 'Biblical women exercise major charisms without being identified with sacramental priesthood',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'charisms-within-sacramental-tradition',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'gifted-for-all-pastoral-offices', linked: true },
                    { type: 'overlaps with', target: 'male-pastor-elder-office', linked: true },
                    {
                        type: 'raises',
                        label: 'How should biblical women shape church leadership?',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'ag-women-leadership',
                'Assemblies of God (USA)',
                'Women in Ministerial Leadership',
                'An official Pentecostal position paper affirming women’s spiritual gifting and eligibility for all aspects and levels of church ministry and leadership.',
                'https://ag.org/en/Beliefs/Position-Papers/Women-in-Ministerial-Leadership',
            ),
            source(
                'church-england-women-bishops',
                'Church of England',
                'House of Bishops’ Declaration on the Ministry of Bishops and Priests and the Five Guiding Principles',
                'An official Anglican statement recording the removal of gender distinctions for bishops, priests, and deacons while providing space for continuing theological disagreement.',
                'https://www.churchofengland.org/about/governance/general-synod/structure/house-bishops/house-bishops-declaration-ministry-bishops-and-priests-and-five-guiding-principles',
            ),
            source(
                'payne-one-in-christ',
                'Philip B. Payne',
                'Man and Woman, One in Christ',
                'A detailed egalitarian exegesis of Paul’s letters arguing for the equal standing and ministry of women and men in church and home.',
                'https://www.zondervan.com/9780310219880/man-and-woman-one-in-christ/',
            ),
            source(
                'sbc-pastor-elder',
                'Southern Baptist Convention',
                'On the Office and Function of Pastor/Elder/Overseer',
                'An official Baptist resolution identifying pastor, elder, and overseer as one office and reserving that office to qualified men.',
                'https://www.sbc.net/resource-library/resolutions/on-the-office-and-function-of-pastor-elder-overseer/',
            ),
            source(
                'women-in-the-church',
                'Andreas J. Köstenberger and Thomas R. Schreiner, editors',
                'Women in the Church: An Interpretation and Application of 1 Timothy 2:9–15',
                'A complementarian academic volume examining the Ephesian setting, syntax, authority language, creation rationale, and contemporary application of 1 Timothy 2.',
                'https://www.crossway.org/books/women-in-the-church-tpb/',
            ),
            source(
                'gilbert-women-pastors',
                'Greg Gilbert',
                'Can Women Be Pastors?',
                'A concise complementarian case connecting Genesis, 1 Timothy 2, and the pastoral office.',
                'https://www.crossway.org/books/can-women-be-pastors-tpb/',
            ),
            source(
                'ordinatio-sacerdotalis',
                'Pope John Paul II',
                'Ordinatio Sacerdotalis',
                'An official Catholic statement reserving priestly ordination to men and explaining the appeal to Christ’s practice and the church’s received apostolic tradition.',
                'https://www.vatican.va/content/john-paul-ii/en/apost_letters/1994/documents/hf_jp-ii_apl_19940522_ordinatio-sacerdotalis.html',
            ),
        ],
        generation,
    },
    {
        slug: 'what-does-1-timothy-2-12-mean',
        category: 'Church Leadership',
        question: 'What does 1 Timothy 2:12 mean?',
        shortTitle: 'The meaning of 1 Timothy 2:12',
        summary: 'In 1 Timothy 2:12 Paul says that he does not permit a woman to teach or to exercise—or assume—authority over a man. Interpreters disagree over whether these are two enduring restrictions that define church order, one combined prohibition against assuming authority to teach, or a temporary response to false teaching and religious disorder among women in Ephesus.',
        introduction: 'The disagreement cannot be resolved by verse 12 in isolation. Interpreters weigh the letter’s concern with false teaching, the command that women learn, the rare verb authentein, Paul’s use of “nor”, the appeal to Adam and Eve, the difficult statement about childbearing, women praying and prophesying elsewhere, and the qualifications for overseers in the next chapter. The readings below include enduring office boundaries, a combined teaching-authority prohibition, a local Ephesian correction, John Dickson’s distinction between traditioning and other forms of speech, a kingdom-focused reframing, a household-scope reading, and a minority proposal that treats the instruction as a Torah-shaped sign-act. These accounts overlap in places but assign different weight to the evidence.',
        seoDescription: 'Compare the main interpretations of 1 Timothy 2:12: enduring male eldership, assumed authority, and a local Ephesian restriction.',
        lastModified: '2026-08-27',
        keyTexts: [
            { label: 'Acts 19:21–41', href: '/read/Acts.19/#Acts.19.21-Acts.19.41' },
            { label: '1 Timothy 1:3–11', href: '/read/1Tim.1/#1Tim.1.3-1Tim.1.11' },
            { label: '1 Timothy 2:8–15', href: '/read/1Tim.2/#1Tim.2.8-1Tim.2.15' },
            { label: '1 Timothy 3:1–13', href: '/read/1Tim.3/#1Tim.3.1-1Tim.3.13' },
        ],
        readings: [
            {
                id: 'enduring-church-order',
                title: 'Women should not teach or exercise authority over men',
                thesis: 'Paul permanently prohibits women from performing the authoritative teaching and governing functions assigned to elders in the gathered church.',
                accent: 'navy',
                badges: ['Complementarian', 'Conservative', 'Baptist', 'Reformed'],
                summary: 'This reading treats “to teach” and “to exercise authority” as two closely related but distinct activities. The following appeal to Adam’s prior creation is taken as a theological rationale rather than a local educational concern, and 1 Timothy 3 is read as naming the qualified male office that carries these responsibilities. “Quietness” means a receptive, peaceable posture, not absolute silence or a ban on every form of female speech.',
                contribution: 'It takes seriously the syntax favoured by many interpreters, Paul’s appeal to Genesis, and the movement from worship instructions into overseer qualifications. It also limits the prohibition to recognised church authority rather than extending it to women teaching any subject or every man exercising authority over every woman.',
                caution: 'The reading must explain why the letter’s immediate false-teaching crisis does not substantially limit the command, how women’s prophecy and teaching elsewhere fit its distinction, and whether the male wording of household qualifications is intended to exclude women rather than describe the ordinary candidate.',
                supporters: [
                    { name: 'Thomas R. Schreiner and Andreas J. Köstenberger', sourceId: 'crossway-1-timothy' },
                    { name: 'Southern Baptist Convention', sourceId: 'sbc-1-timothy' },
                ],
                predicates: [
                    {
                        label: 'Pastor, elder, and overseer form a governing and teaching office reserved to qualified men',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'male-pastor-elder-office',
                        linked: true,
                    },
                    {
                        label: 'Women’s biblical ministries do not erase the distinction of the pastor-elder office',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'broad-ministry-with-office-distinction',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'combined-prohibition-on-assumed-authority', linked: true },
                    { type: 'contrasts with', target: 'local-ephesian-correction', linked: true },
                    {
                        type: 'raises',
                        label: 'Can women be pastors?',
                        topicSlug: 'can-women-be-pastors',
                        linked: true,
                    },
                ],
            },
            {
                id: 'combined-prohibition-on-assumed-authority',
                title: 'Paul prohibits women from assuming teaching authority',
                thesis: 'Paul prohibits women from taking authority to teach men for themselves, not from teaching when they are properly called and authorised by the church.',
                accent: 'teal',
                badges: ['Egalitarian', 'Evangelical', 'Linguistic', 'Moderate'],
                summary: 'This interpretation argues that Paul’s grammar joins teaching and authentein into one combined idea. On that reading, the problem is not two universally forbidden activities but teaching undertaken with assumed or seized authority. The command that a woman learn is therefore constructive: formation and accountable authorisation, rather than sex alone, determine when a person may teach.',
                contribution: 'It offers a syntactically argued way to hold verse 12 together with Paul’s recognition of female coworkers, prophets, teachers, and ministers. It also keeps authority accountable to the church: neither women nor men possess a private right to teach simply because they desire influence.',
                caution: 'Critics dispute whether authentein carried an inherently negative sense and whether Paul’s use of “nor” makes the two verbs one prohibited action. The view must also explain the appeal to creation and show that “authorised teaching” is present in the passage rather than supplied to solve a modern problem.',
                supporters: [
                    { name: 'Philip B. Payne', sourceId: 'payne-single-idea' },
                ],
                predicates: [
                    {
                        label: 'Women may hold pastoral office when their teaching is called, formed, and recognised by the church',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'gifted-for-all-pastoral-offices',
                        linked: true,
                    },
                    {
                        label: 'Priscilla, Phoebe, Junia, and other women provide evidence of recognised ministry rather than self-appointed authority',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'patterns-of-authoritative-ministry',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'enduring-church-order', linked: true },
                    { type: 'overlaps with', target: 'local-ephesian-correction', linked: true },
                    {
                        type: 'raises',
                        label: 'What do women leaders in the Bible show?',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        linked: true,
                    },
                ],
            },
            {
                id: 'local-ephesian-correction',
                title: 'Paul addresses a local crisis in Ephesus',
                thesis: 'Paul temporarily restricts women implicated in false teaching or local religious disorder while requiring that they first learn in quietness.',
                accent: 'amber',
                badges: ['Egalitarian', 'Contextual', 'Progressive', 'Historical'],
                summary: 'This reading begins with 1 Timothy’s repeated concern about false teaching and the distinctive religious world of Ephesus. Paul’s first positive instruction is that a woman should learn. The restriction that follows addresses a damaged setting in which some women lacked formation or repeated teaching shaped by the surrounding culture; the Adam and Eve reference corrects deception and claims about origins rather than establishing male rule for every church.',
                contribution: 'It reads the paragraph as pastoral intervention within the letter’s stated crisis and gives explanatory weight to the command to learn, the Ephesian setting, and the difficult childbearing language. It can account for why Paul commends women in active ministry elsewhere without treating him as inconsistent.',
                caution: 'Reconstruction of local conditions can become speculative, and the text never explicitly says “until these women are educated”. The view must show that its historical account is probable enough to limit a command that Paul supports by retelling Genesis.',
                supporters: [
                    { name: 'Sandra L. Glahn', sourceId: 'glahn-ephesus' },
                    { name: 'Linda L. Belleville and egalitarian contributors', sourceId: 'discovering-equality' },
                ],
                predicates: [
                    {
                        label: 'Pastoral eligibility should be discerned through calling, gifting, formation, and character rather than sex',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'gifted-for-all-pastoral-offices',
                        linked: true,
                    },
                    {
                        label: 'The wider biblical pattern of women’s ministry supplies the canonical context for a local restriction',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'patterns-of-authoritative-ministry',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'enduring-church-order', linked: true },
                    { type: 'overlaps with', target: 'combined-prohibition-on-assumed-authority', linked: true },
                    {
                        type: 'raises',
                        label: 'How should culturally specific apostolic commands be applied?',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        linked: true,
                    },
                ],
            },
            {
                id: 'dickson-traditioning-distinction',
                title: 'The restriction concerns authoritative traditioning, not every sermon',
                thesis: 'Paul restricts the entrusted work of authoritatively transmitting apostolic teaching, while other forms of public Christian speech may remain open to women.',
                accent: 'teal',
                badges: ['Complementarian', 'Conservative', 'Anglican', 'Evangelical'],
                summary: 'John Dickson argues that modern Christians too quickly equate Paul’s “teaching” with every sermon or act of exposition. He distinguishes the specialised work of authoritatively preserving and transmitting the apostolic deposit from prophecy, exhortation, testimony, and other forms of ministry speech. On this account, women may give sermons or exhortations, while the church’s recognised authority to guard and hand on its foundational teaching remains bounded in the way Dickson understands 1 Timothy 2 and elder oversight.',
                contribution: 'This reading explains why Paul can restrict “teaching” in 1 Timothy 2 while also recognising women praying, prophesying, instructing, and working as trusted coworkers. It also gives churches a more precise question than “May a woman speak?”: what kind of speech carries the church’s formal responsibility for the apostolic deposit?',
                caution: 'The distinction depends on a contested account of didaskō in the Pastorals and can make the boundary between a sermon and authoritative traditioning difficult to police. Critics also argue that the New Testament does not draw the line as sharply as this proposal requires.',
                supporters: [
                    { name: 'John Dickson', sourceId: 'dickson-traditioning' },
                ],
                predicates: [
                    {
                        label: 'Different kinds of Christian speech carry different levels and kinds of authority',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'broad-ministry-with-office-distinction',
                        linked: true,
                    },
                    {
                        label: 'Formal doctrinal guardianship belongs to a recognised church office',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'male-pastor-elder-office',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'enduring-church-order', linked: true },
                    { type: 'overlaps with', target: 'combined-prohibition-on-assumed-authority', linked: true },
                ],
            },
            {
                id: 'kingdom-reframing-beyond-binary',
                title: 'The debate should be reframed around kingdom life',
                thesis: 'The central question is not simply whether women possess equal rights or whether men possess authority, but how the church embodies kingdom unity, corporate identity, service, and reversal.',
                accent: 'amber',
                badges: ['Neither Complementarian nor Egalitarian', 'Kingdom Theology', 'Moderate', 'Evangelical'],
                summary: 'Michelle Lee-Barnewall’s Neither Complementarian nor Egalitarian does not offer a technical verse-by-verse solution to 1 Timothy 2:12. Instead, it argues that the familiar complementarian-versus-egalitarian binary can obscure Scripture’s deeper categories: the unity and corporate identity of God’s people, service rather than status, and the kingdom’s reversal of ordinary power. Applied to this passage, the reading asks what kind of communal life Paul is forming before deciding whether the text maps neatly onto modern debates about rights, leadership, and office.',
                contribution: 'It exposes the risk of importing modern individualist categories into an ancient ecclesial text and gives the page a genuinely third framework rather than another version of one of the two standard camps. It is particularly useful for assessing how authority should be exercised, even where readers still reach different conclusions about women teaching or leading.',
                caution: 'Because Lee-Barnewall intentionally does not centre a detailed exegesis of 1 Timothy 2:11–15, this reading should not be presented as a direct alternative translation of verse 12. It reframes the debate and may leave practical questions about office and teaching unresolved.',
                supporters: [
                    { name: 'Michelle Lee-Barnewall', sourceId: 'lee-barnewall-kingdom' },
                ],
                predicates: [
                    {
                        label: 'Questions of authority should be interpreted within the church’s corporate identity and kingdom vocation',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'ecclesial-reception',
                        linked: true,
                    },
                    {
                        label: 'Gender texts should be read through the wider biblical pattern of unity, service, and reversal',
                        topicSlug: 'what-do-women-leaders-in-the-bible-show',
                        readingId: 'patterns-of-authoritative-ministry',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'enduring-church-order', linked: true },
                    { type: 'qualifies', target: 'local-ephesian-correction', linked: true },
                    {
                        type: 'raises',
                        label: 'Can women be pastors?',
                        topicSlug: 'can-women-be-pastors',
                        linked: true,
                    },
                ],
            },
            {
                id: 'household-husband-wife-scope',
                title: 'The instruction concerns wives and their husbands',
                thesis: 'Paul addresses a household relationship, restricting a wife from teaching or taking authority over her own husband rather than forbidding every woman from teaching every man.',
                accent: 'navy',
                badges: ['Household Reading', 'Contextual', 'Complementarian', 'Moderate'],
                summary: 'This reading gives γυνή and ἀνήρ their relational meanings of “wife” and “husband” in a passage that sits near household and church-order concerns. Paul would then be regulating the relationship between a wife and her husband, perhaps especially where a household’s public learning or leadership affected the congregation. It does not by itself settle whether women may teach other men, serve as elders, or exercise authority in a wider church setting.',
                contribution: 'It notices that Greek terms can refer to women and men generally or to wives and husbands specifically, and it takes seriously the possibility that family relationships shape the examples and instructions around 1 Timothy 2–3. It also offers a narrower account of the command than a universal male-over-female rule.',
                caution: 'The singular nouns do not automatically mean spouses, and the references to prayer, worship, women generally, and “men everywhere” may point beyond a private marriage. The reading must also explain why the passage’s immediate context is ecclesial and why the office qualifications follow it.',
                supporters: [
                    { name: 'Household-scope interpreters', sourceId: 'household-scope-study' },
                ],
                predicates: [
                    {
                        label: 'Household instructions can shape but should not automatically define the church’s public offices',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'gifted-for-all-pastoral-offices',
                        linked: true,
                    },
                    {
                        label: 'The meaning of woman and man must be established from context rather than assumed from English translations',
                        topicSlug: 'what-does-1-timothy-2-12-mean',
                        readingId: 'combined-prohibition-on-assumed-authority',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'enduring-church-order', linked: true },
                    { type: 'qualifies', target: 'local-ephesian-correction', linked: true },
                ],
            },
            {
                id: 'torah-shaped-sign-act',
                title: 'The restriction performs a Torah-shaped sign-act',
                thesis: 'Paul uses a gendered posture of learning and silence as a visible enactment of the Law’s creation and household order, rather than offering a timeless description of women’s capacity or worth.',
                accent: 'red',
                badges: ['Torah-shaped', 'Jewish Context', 'Symbolic Reading', 'Minority View'],
                summary: 'This minority proposal reads 1 Timothy 2:11–14 alongside 1 Corinthians 14:34–35, where the writer appeals to “the Law”, and asks whether the instruction functions as a public sign-act. The assembly temporarily performs an ordered world associated with Genesis and Torah: women learn in a submissive posture, and the community displays a scriptural pattern of male household leadership. On this account, the practice is theological theatre or covenantal pedagogy—acting out the Torah’s world to make a point—rather than a direct statement that women are inherently unable to teach.',
                contribution: 'It takes the explicit appeal to “the Law” in 1 Corinthians 14 seriously and draws attention to the symbolic and pedagogical force of embodied practices in Jewish and early Christian communities. It can also explain why Paul’s instructions may be visible, communal, and rhetorically charged without reducing them to a general theory of female inferiority.',
                caution: 'The sign-act proposal is not a widely established consensus interpretation, and 1 Timothy 2 itself appeals to creation in a way many readers take as normative. The relationship between 1 Timothy 2 and 1 Corinthians 14 is also debated, as is which text or tradition “the Law” refers to; the proposal should therefore be marked as exploratory rather than presented as the plain meaning.',
                supporters: [
                    { name: 'L. Ann Jervis', sourceId: 'jervis-corinthian-law' },
                ],
                predicates: [
                    {
                        label: 'Commands can preserve a theological purpose through a culturally embodied sign or practice',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'preserve-purpose-adapt-form',
                        linked: true,
                    },
                    {
                        label: 'Paul’s appeal to the Law must be interpreted within Jewish scriptural and communal patterns',
                        topicSlug: 'what-does-paul-mean-by-works-of-the-law',
                        readingId: 'boundary-markers',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'enduring-church-order', linked: true },
                    { type: 'overlaps with', target: 'household-husband-wife-scope', linked: true },
                    {
                        type: 'raises',
                        label: 'How should culturally specific apostolic commands be applied?',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'crossway-1-timothy',
                'Andreas J. Köstenberger and Thomas R. Schreiner, editors',
                'Women in the Church: An Interpretation and Application of 1 Timothy 2:9–15',
                'A complementarian academic treatment of the passage’s setting, grammar, authority language, creation rationale, and continuing application.',
                'https://www.crossway.org/books/women-in-the-church-tpb/',
            ),
            source(
                'sbc-1-timothy',
                'Southern Baptist Convention',
                'On the Office and Function of Pastor/Elder/Overseer',
                'An official Baptist application of 1 Timothy 2–3 to a male pastor-elder-overseer office.',
                'https://www.sbc.net/resource-library/resolutions/on-the-office-and-function-of-pastor-elder-overseer/',
            ),
            source(
                'payne-single-idea',
                'Philip B. Payne',
                '1 Tim 2.12 and the Use of οὐδέ to Combine Two Elements to Express a Single Idea',
                'A peer-reviewed grammatical argument that teaching and assuming authority form one combined prohibition in 1 Timothy 2:12.',
                'https://www.cambridge.org/core/journals/new-testament-studies/article/1-tim-212-and-the-use-of-to-combine-two-elements-to-express-a-single-idea/088810B9D987D8797FF1CB11F71F7E7D',
            ),
            source(
                'glahn-ephesus',
                'Sandra L. Glahn',
                'Nobody’s Mother: Artemis of the Ephesians in Antiquity and the New Testament',
                'An academic study using literary, epigraphic, architectural, and exegetical evidence to place 1 Timothy 2 within the religious world of Ephesus.',
                'https://shop.ivpress.com/nobody-s-mother',
            ),
            source(
                'discovering-equality',
                'Ronald W. Pierce and Cynthia Long Westfall, editors',
                'Discovering Biblical Equality',
                'An egalitarian academic collection with chapters on women leaders, 1 Timothy 2, spiritual gifts, authority, and ministry practice.',
                'https://www.ivpress.com/discovering-biblical-equality',
            ),
            source(
                'dickson-traditioning',
                'John Dickson',
                '“Teaching” as traditioning in 1 Timothy 2:12: An historical observation',
                'A peer-reviewed chapter arguing that Paul’s “teaching” can refer to the authoritative transmission of apostolic tradition rather than every form of Christian speech or sermon.',
                'https://researchers.mq.edu.au/en/publications/teaching-as-traditioning-in-1-timothy-2-an-historical-observat/',
            ),
            source(
                'lee-barnewall-kingdom',
                'Michelle Lee-Barnewall',
                'Neither Complementarian nor Egalitarian: A Kingdom Corrective to the Evangelical Gender Debate',
                'A theological proposal to reframe gender debates around kingdom themes including unity, corporate identity, service, and reversal; the book intentionally does not offer a detailed verse-by-verse treatment of 1 Timothy 2:12.',
                'https://bakerpublishinggroup.com/products/9780801039577_neither-complementarian-nor-egalitarian',
            ),
            source(
                'household-scope-study',
                'Stellenbosch University research repository',
                'Women, Teaching and Leadership in 1 Timothy 2:11–15: A rhetorical-critical study',
                'An academic study that surveys the household-scope proposal, including the possibility that woman and man may refer to wife and husband, alongside the passage’s wider ecclesial context.',
                'https://scholar.sun.ac.za/bitstream/10019.1/106008/1/david_women_2019.pdf',
            ),
            source(
                'jervis-corinthian-law',
                'L. Ann Jervis',
                '1 Corinthians 14.34–35: A Reconsideration of Paul’s Limitation of the Free Speech of Some Corinthian Women',
                'A peer-reviewed study that treats the passage as Paul’s own words, reads the silence within the context of ordered charismatic worship, and examines the possible Jewish scriptural background of the appeal to “the Law”.',
                'https://journals.sagepub.com/doi/10.1177/0142064X9501705804',
            ),
            source(
                'payne-one-in-christ-1-timothy',
                'Philip B. Payne',
                'Man and Woman, One in Christ',
                'A wider exegetical treatment of Paul’s teaching on women, including 1 Timothy 2–3 and ministry leadership.',
                'https://www.zondervan.com/9780310219880/man-and-woman-one-in-christ/',
            ),
        ],
        generation: { ...generation, date: '27 August 2026' },
    },
    {
        slug: 'what-do-women-leaders-in-the-bible-show',
        category: 'Church Leadership',
        question: 'What do women leaders in the Bible show about women pastors today?',
        shortTitle: 'Women leaders in the Bible and pastors today',
        summary: 'Scripture portrays women as prophets, judges, patrons, missionaries, teachers, coworkers, deacons or ministers, and possibly an apostle. Christians disagree over whether these examples establish women’s eligibility for pastoral office, demonstrate broad ministry while leaving pastor-eldership male, or display charisms that remain distinct from sacramental priesthood.',
        introduction: 'Lists of biblical women do not interpret themselves. The debate asks what titles such as diakonos and apostolos mean in context, what authority prophecy and teaching carried, whether house-church patronage implied leadership, and how narrative examples relate to direct instructions about teaching and overseers. A responsible reading must neither erase the women Scripture names nor assign them modern job descriptions without evidence.',
        seoDescription: 'Explore what Deborah, Priscilla, Phoebe, Junia, women prophets, and Paul’s coworkers contribute to the women-pastors debate.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Judges 4:1–24', href: '/read/Judg.4/#Judg.4.1-Judg.4.24' },
            { label: 'Acts 18:18–28', href: '/read/Acts.18/#Acts.18.18-Acts.18.28' },
            { label: 'Romans 16:1–16', href: '/read/Rom.16/#Rom.16.1-Rom.16.16' },
            { label: '1 Corinthians 11:2–16', href: '/read/1Cor.11/#1Cor.11.2-1Cor.11.16' },
        ],
        readings: [
            {
                id: 'patterns-of-authoritative-ministry',
                title: 'Women exercise authoritative ministry in Scripture',
                thesis: 'Biblical women lead, teach, speak God’s word, and represent churches in ways that support women’s eligibility for every pastoral office.',
                accent: 'teal',
                badges: ['Egalitarian', 'Pentecostal', 'Progressive', 'Biblical Theology'],
                summary: 'This reading treats Deborah’s judging and prophecy, Huldah’s authoritative word, Priscilla’s instruction of Apollos, Phoebe’s ministry and patronage, Junia’s apostolic association, and women’s prayer and prophecy as a cumulative pattern. Paul does not merely tolerate women’s labour; he publicly commends them as coworkers and entrusts them with consequential ministry. Restrictive passages should therefore be read as responses to particular disorder rather than the rule that defines all these examples.',
                contribution: 'It recovers named women who can disappear from leadership discussions and evaluates the whole biblical witness rather than beginning and ending with two disputed prohibitions. It also shows that authoritative spiritual speech and mission were never uniformly male activities.',
                caution: 'The view must avoid converting every prophetic, apostolic, patronal, or household role into the later office of pastor and must still explain passages that appear to regulate teaching and oversight by sex.',
                supporters: [
                    { name: 'Nijay K. Gupta', sourceId: 'gupta-tell-her-story' },
                    { name: 'Assemblies of God (USA)', sourceId: 'ag-biblical-women' },
                ],
                predicates: [
                    {
                        label: 'Pastoral office is open to women whose calling and gifts are recognised by the church',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'gifted-for-all-pastoral-offices',
                        linked: true,
                    },
                    {
                        label: '1 Timothy 2 responds to a local crisis rather than cancelling the wider pattern',
                        topicSlug: 'what-does-1-timothy-2-12-mean',
                        readingId: 'local-ephesian-correction',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'broad-ministry-with-office-distinction', linked: true },
                    { type: 'contrasts with', target: 'charisms-within-sacramental-tradition', linked: true },
                    {
                        type: 'raises',
                        label: 'Can women be pastors?',
                        topicSlug: 'can-women-be-pastors',
                        linked: true,
                    },
                ],
            },
            {
                id: 'broad-ministry-with-office-distinction',
                title: 'Women may minister broadly without being pastor-elders',
                thesis: 'Biblical women exercise important and sometimes authoritative ministries, but none is unambiguously appointed to the governing pastor-elder office described in 1 Timothy 3 and Titus 1.',
                accent: 'navy',
                badges: ['Complementarian', 'Conservative', 'Baptist', 'Reformed'],
                summary: 'This reading affirms the significance of Deborah, Priscilla, Phoebe, Junia, and women prophets while distinguishing their ministries from the local office of elder or overseer. Prophecy is not identical to the elder’s responsibility to preserve and teach doctrine; “apostle” may mean messenger; and diakonos need not mean elder. These examples expand women’s ministry but do not override direct instructions about church order.',
                contribution: 'It prevents a male-elder position from becoming a claim that women did little, possessed no authority, or should remain invisible. It also asks careful lexical and institutional questions before mapping first-century descriptions directly onto modern pastoral titles.',
                caution: 'The approach can minimise the force of titles and commendations whenever a woman appears and then define the decisive office mainly from male examples. It must demonstrate that its office distinctions arise from the texts rather than from a conclusion already assumed.',
                supporters: [
                    { name: 'Southern Baptist Convention', sourceId: 'sbc-biblical-women' },
                    { name: 'Thomas R. Schreiner and Andreas J. Köstenberger', sourceId: 'crossway-biblical-women' },
                ],
                predicates: [
                    {
                        label: 'Pastor, elder, and overseer are one office reserved to qualified men',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'male-pastor-elder-office',
                        linked: true,
                    },
                    {
                        label: '1 Timothy 2 establishes the enduring boundary around authoritative elder teaching',
                        topicSlug: 'what-does-1-timothy-2-12-mean',
                        readingId: 'enduring-church-order',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'patterns-of-authoritative-ministry', linked: true },
                    { type: 'overlaps with', target: 'charisms-within-sacramental-tradition', linked: true },
                    {
                        type: 'raises',
                        label: 'What does 1 Timothy 2:12 mean?',
                        topicSlug: 'what-does-1-timothy-2-12-mean',
                        linked: true,
                    },
                ],
            },
            {
                id: 'charisms-within-sacramental-tradition',
                title: 'Women’s gifts flourish without ordained priesthood',
                thesis: 'Biblical women demonstrate that prophecy, teaching, mission, patronage, and holiness are not male preserves, while apostolic and sacramental tradition still reserves priestly ordination to men.',
                accent: 'red',
                badges: ['Catholic', 'Sacramental', 'Traditional', 'Ecclesial'],
                summary: 'This account welcomes the full biblical witness to women’s service without treating every ministry as priestly ordination. Mary Magdalene’s resurrection witness, Priscilla’s instruction, women’s prophecy, and Paul’s coworkers show indispensable charisms. The ordained priesthood is nevertheless interpreted through Christ’s choice of the Twelve and the church’s continuing sacramental practice rather than inferred directly from whether women taught or led in other ways.',
                contribution: 'It protects the significance of women’s biblical ministries while making explicit that sacramental ordination belongs to a larger theology of church, apostolic succession, and representation. It also refuses the inference that exclusion from priesthood means inferiority in holiness or Christian vocation.',
                caution: 'Critics argue that the distinction can praise women’s gifts while structurally excluding them from decisive authority, and they question whether the male Twelve or later reception establishes a permanent divine limit on ordination.',
                supporters: [
                    { name: 'Pope John Paul II', sourceId: 'ordinatio-biblical-women' },
                    { name: 'Sacred Congregation for the Doctrine of the Faith', sourceId: 'inter-insigniores' },
                ],
                predicates: [
                    {
                        label: 'Priestly ordination is a sacramental office the church understands itself unable to confer upon women',
                        topicSlug: 'can-women-be-pastors',
                        readingId: 'male-sacramental-priesthood',
                        linked: true,
                    },
                    {
                        label: 'The meaning of apostolic examples is received through Scripture and the church’s living tradition',
                        topicSlug: 'how-should-christians-apply-culturally-specific-apostolic-commands',
                        readingId: 'ecclesial-reception',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'patterns-of-authoritative-ministry', linked: true },
                    { type: 'overlaps with', target: 'broad-ministry-with-office-distinction', linked: true },
                    {
                        type: 'raises',
                        label: 'Can women be pastors?',
                        topicSlug: 'can-women-be-pastors',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            source(
                'gupta-tell-her-story',
                'Nijay K. Gupta',
                'Tell Her Story: How Women Led, Taught, and Ministered in the Early Church',
                'An academic study of women including Phoebe, Prisca, Junia, and Nympha as active ministers and leaders in the earliest churches.',
                'https://www.ivpress.com/tell-her-story',
            ),
            source(
                'ag-biblical-women',
                'Assemblies of God (USA)',
                'Women in Ministerial Leadership',
                'An official Pentecostal survey of biblical women and Spirit gifting used to support women serving at every level of church leadership.',
                'https://ag.org/en/Beliefs/Position-Papers/Women-in-Ministerial-Leadership',
            ),
            source(
                'sbc-biblical-women',
                'Southern Baptist Convention',
                'On the Office and Function of Pastor/Elder/Overseer',
                'An official Baptist statement distinguishing the male pastor-elder-overseer office from the wider ministry of the church.',
                'https://www.sbc.net/resource-library/resolutions/on-the-office-and-function-of-pastor-elder-overseer/',
            ),
            source(
                'crossway-biblical-women',
                'Andreas J. Köstenberger and Thomas R. Schreiner, editors',
                'Women in the Church: An Interpretation and Application of 1 Timothy 2:9–15',
                'A complementarian treatment that interprets women’s ministries within an enduring distinction around elder teaching and authority.',
                'https://www.crossway.org/books/women-in-the-church-tpb/',
            ),
            source(
                'ordinatio-biblical-women',
                'Pope John Paul II',
                'Ordinatio Sacerdotalis',
                'An official Catholic statement distinguishing women’s equal dignity and holiness from eligibility for sacramental priestly ordination.',
                'https://www.vatican.va/content/john-paul-ii/en/apost_letters/1994/documents/hf_jp-ii_apl_19940522_ordinatio-sacerdotalis.html',
            ),
            source(
                'inter-insigniores',
                'Sacred Congregation for the Doctrine of the Faith',
                'Declaration on the Question of Admission of Women to the Ministerial Priesthood',
                'An official Catholic explanation of the received male priesthood, Christ’s choice of apostles, women’s biblical roles, and sacramental representation.',
                'https://www.vatican.va/roman_curia/congregations/cfaith/documents/rc_con_cfaith_doc_19761015_inter-insigniores_en.html',
            ),
            source(
                'finding-phoebe',
                'Susan E. Hylen',
                'Finding Phoebe: What New Testament Women Were Really Like',
                'A historical study challenging passive assumptions about New Testament women and examining their leadership, patronage, and social agency.',
                'https://www.eerdmans.com/9780802882066/finding-phoebe/',
            ),
        ],
        generation,
    },
];
